import { WebStorageStateStore } from 'oidc-client-ts'
import type { ReactNode } from 'react'
import { AuthProvider, type AuthProviderProps } from 'react-oidc-context'
import { cognitoConfig } from '@/lib/cognito-config'
import { AuthStateGate } from './AuthStateGate'
import { AuthBridgeRegistrar } from './auth-bridge-registrar'

// Single OIDC provider + callback owner (ADR-0011). `react-oidc-context`
// AuthProvider is the ONLY thing that processes the Authorization Code + PKCE
// callback; no component calls signinRedirectCallback(). Tokens live only in the
// library's sessionStorage-backed store.

/**
 * Strip the OAuth callback query params after the library completes the exchange.
 * The intended route is restored later (after `/users/auth/me`), NOT here. Never logs
 * code/state/token/error.
 */
const onSigninCallback: AuthProviderProps['onSigninCallback'] = () => {
	window.history.replaceState({}, document.title, window.location.pathname)
}

// Authorization Code Flow with PKCE; tokens in a sessionStorage-backed store;
// no silent renew.
const userStore = new WebStorageStateStore({ store: window.sessionStorage })

function buildAuthProviderSettings(): AuthProviderProps {
	// `App.tsx` fails closed to `ConfigErrorScreen` whenever Cognito is not configured
	// (ADR-0011), so this provider is only ever reached with valid config.
	// This guard only fires on an accidental direct render bypassing that gate — it
	// must never fabricate placeholder OIDC settings.
	if (!cognitoConfig) {
		throw new Error('OidcAuthProvider rendered without a valid Cognito configuration')
	}

	return {
		authority: cognitoConfig.authority,
		client_id: cognitoConfig.clientId,
		redirect_uri: cognitoConfig.redirectUri,
		scope: cognitoConfig.scope,
		response_type: 'code',
		automaticSilentRenew: false,
		userStore,
		onSigninCallback,
	}
}

export function OidcAuthProvider({ children }: { children: ReactNode }) {
	// `AuthBridgeRegistrar` connects the provider-owned OIDC user to the Axios client;
	// `AuthStateGate` renders terminal auth states (inactive / not-provisioned) instead
	// of the router. Both must live inside `AuthProvider` (and the outer QueryClient).
	return (
		<AuthProvider {...buildAuthProviderSettings()}>
			<AuthBridgeRegistrar />
			<AuthStateGate>{children}</AuthStateGate>
		</AuthProvider>
	)
}
